import Vue from 'vue'
import Vuex from 'vuex'
import Client from '@/api/client'

Vue.use(Vuex)

export default new Vuex.Store({
  state: {
    config: {
      debug: false,
      surveyAddress: '',
      widgetLanguage: '',
      widgetColor: '',
      widgetPosition: 'default',
      widgetDisplayFooter: true
    },
    ui: {
      isWidget: false,
      isEmbed: false,
      step: 'loading', // loading, emotion, details, sending, end
      selectedLanguage: 'fi',
      languageSupport: {
        finnish: true,
        swedish: false,
        english: false,
        russian: false,
        estonian: false,
        german: false,
        turkish: false
      },
      isSendingAnswer: false,
      statsLoaded: false,
      windowWidth: 0,
      windowHeight: 0,
      emotionBackground: null,
      emotionsImage: null
    },
    answer: {
      project_id: 100,
      emotion_id: 22,
      emotionGroup: 'enthusiastic',
      emotion: 'Innostunut',
      language: 'fi',
      referenceCode: '',
      questions: [
        null,
        null,
        { // a2
          value: '',
          extra: ''
        },
        { // a3
          value: '',
          extra: ''
        },
        { // a4
          value: '',
          extra: ''
        },
        { // a5
          value: '',
          extra: ''
        },
        { // a6
          value: '',
          extra: ''
        },
        { // a7
          value: '',
          extra: ''
        },
        { // a8
          value: '',
          extra: ''
        }
        // extra_questions: a9, a10, a11, etc
      ]
    },
    survey: {},
    questions: {
      fi: [
        {
          'id': 0,
          'q_id': 1,
          'project_id': 0,
          'question': 'Mikä fiilis?',
          'question_type': 'feeling',
          'language': 'fi',
          'question_options': null,
          'language_options': null
        }
      ]
    },
    surveyProfile: {}
  },
  getters: {
    // UI
    isWidget: (state) => {
      return state.ui.isWidget
    },
    isEmbed: (state) => {
      return state.ui.isEmbed
    },
    displayFooter: (state) => {
      return state.config.widgetDisplayFooter
    },
    config: (state) => {
      return state.config
    },
    widgetPosition: (state) => {
      return state.config.widgetPosition
    },
    isSendingAnswer: (state) => {
      return state.ui.isSendingAnswer
    },

    // Widget button/emotion selection background
    emotionBackground: (state) => {
      return state.ui.emotionBackground
    },
    emotionsImage: (state) => {
      return state.ui.emotionsImage
    },
    backgroundColor: (state) => {
      return state.config.widgetColor
    },


    // Emotion selection
    windowWidth: (state) => {
      return state.ui.windowWidth
    },
    windowHeight: (state) => {
      return state.ui.windowHeight
    },
    windowRatio: (state, getters) => {
      return getters.windowWidth / getters.windowHeight
    },

    // Survey content
    survey: (state) => {
      return state.survey
    },
    areStatsLoaded: (state) => {
      return state.ui.statsLoaded
    },
    emotionStats: (state) => {
      return state.surveyProfile
    },

    // Survey steps
    currentStep: (state) => {
      return state.ui.step
    },
    atLoadingStep: (state, getters) => {
      return getters.currentStep === 'loading'
    },
    atEmotionStep: (state, getters) => {
      return getters.currentStep === 'emotion'
    },
    atDetailsStep: (state, getters) => {
      return getters.currentStep === 'details'
    },
    atSendingStep: (state, getters) => {
      return getters.currentStep === 'sending'
    },
    atEndStep: (state, getters) => {
      return getters.currentStep === 'end'
    },

    // Language selections
    language: (state) => {
      return state.ui.selectedLanguage || 'fi'
    },
    supportedLanguages: (state) => {
      return state.ui.languageSupport
    },

    // Answers
    selectedEmotionGroup: (state) => {
      return state.answer.emotionGroup
    },
    selectedEmotion: (state) => {
      return state.answer.emotion
    },
    answer: (state) => (questionId) => {
      return state.answer.questions[questionId] || {}
    },
    answerValue: (state, getters) => (questionId) => {
      return getters.answer(questionId).value || ''
    },
    answerExtra: (state, getters) => (questionId) => {
      return getters.answer(questionId).extra || ''
    },
    answers: (state) => {
      return state.answer
    },
    mergedAnswer: (state, getters) => (questionId) => {
      let answer = ''
      let answerValue = getters.answer(questionId).value || ''
      let answerExtra = getters.answer(questionId).extra || ''

      // Process main answer
      if (Array.isArray(answerValue)) {
        // Checkboxes as array
        answer = answerValue.join('|')
      } else if (typeof answerValue === 'object') {
        // Checkboxes as object
        let result = []

        Object.keys(answerValue).forEach(key => {
          if (answerValue[key] === true) {
            result.push(key)
          }
        })

        answer = result.join('|')
      } else {
        // Normal value
        answer = answerValue.toString()
      }

      // Process extra answer
      if (answerExtra) {
        answer += ' ' + answerExtra
      }

      return answer
    },

    // Texts/questions
    question: (state, getters) => (questionId) => {
      let currentLanguage = getters.language

      return state.questions[currentLanguage].find(question => question.q_id == questionId)
    },
    detailQuestions: (state, getters) => {
      let currentLanguage = getters.language

      return state.questions[currentLanguage]
    },
    uiTexts: (state, getters) => {
      let currentLanguage = getters.language

      let texts = {
        fi: {
          emotionText: "Tarkenna valintaa vielä klikkauksen verran",
          nextText: "Jatka",
          sendText: "Lähetä",
          endUrl: "index.html",
          endButtonHide: false,
          emotionProfileText: "Kyselyn tunneprofiili",
          thanksText: "Kiitos vastauksistasi!",
          endText: "Sulje kysely",
          otherWhat: "Muu, mikä?"
        },
        se: {
          emotionText: "Fördjupa ditt val genom att välja en känsla nedan",
          nextText: "NÄSTÄ",
          sendText: "SKICKA",
          endUrl: "index.html",
          endButtonHide: false,
          emotionProfileText: "Undersökning känsloprofil",
          thanksText: "Tack för ditt svar!",
          endText: "Tillbaka till början",
          otherWhat: "Skulle du vilja lägga till något? Berätta för oss!"
        },
        en: {
          emotionText: "You can specify the chosen emotion",
          nextText: "Next",
          sendText: "Send",
          emotionProfileText: "Survey emotion profile",
          thanksText: "Thank you for your answers!",
          endText: "Close the Survey",
          otherWhat: "Something else, what?"
        },
        ru: {
          emotionText: "Ваше чувство",
          nextText: "Далее",
          sendText: "Отправить",
          emotionProfileText: "Survey emotion profile",
          thanksText: "Спасибо за Ваш ответ!",
          endText: "Закрыть опрос",
          otherWhat: "Something else, what?"
        },
        de: {
          emotionText: "noch genauer?",
          nextText: "Next",
          sendText: "Abschicken",
          emotionProfileText: "Gefühlsprofil der Anfrage",
          thanksText: "Danke!",
          endText: "Anfrage abschließen",
          otherWhat: "Eine weitere Option, welche?"
        },
        tr: {
          emotionText: "Cevabınızı belirleyebilirsiniz, isterseniz",
          nextText: "Sonraki",
          sendText: "Gönderme",
          emotionProfileText: "Baskalarinin yanıtlarını burda görebilirsiniz",
          thanksText: "Cevaplarınız için teşekkürler!",
          endText: "Anketi tamamlazniniz",
          otherWhat: "Başka bir seçenek?"
        }
      }

      return texts[currentLanguage] || {}
    },
    emotionOptions: (state, getters) => {
      let currentLanguage = getters.language

      let allEmotionOptions = {
        fi: {
          enthusiastic: ["Iloinen", "Innostunut", "Kiinnostunut"],
          surprised: ["Ällistynyt", "Yllättynyt", "Vaikuttunut"],
          irritated: ["Raivostunut", "Ärtynyt", "Närkästynyt"],
          confused: ["Stressaantunut", "Hämmentynyt", "Peloissaan"],
          disappointed: ["Onneton", "Pettynyt", "Tylsistynyt"],
          indifferent: ["Piittaamaton", "Välinpitämätön", "Saamaton"],
          peaceful: ["Luottavainen", "Rauhallinen", "Seesteinen"],
          satisfied: ["Varma", "Tyytyväinen", "Hyväksyvä"]
        },
        se: {
          enthusiastic: ["Glad", "Entusiastisk", "Intresserad"],
          surprised: ["Häpen", "Överraskad", "Imponerad"],
          irritated: ["Rasande", "Förargad", "Upprörd"],
          confused: ["Stressad", "Generad", "Frustrerad"],
          disappointed: ["Olycklig", "Besviken", "Förslöad"],
          indifferent: ["Kallsinnig", "Likgiltig", "Oföretagsam"],
          peaceful: ["förtroendefull", "Lugn", "Fridfull"],
          satisfied: ["Säker", "Nöjd", "Godkännande"]
        },
        es: {
          enthusiastic: ["Rõõmus", "Innukas", "Huvitatud"],
          surprised: ["Rabatud", "Üllatunud", "Muljetavaldav"],
          irritated: ["Frustratsiooni tundev", "Ärritunud", "Nördinud"],
          confused: ["Stressis", "Segaduses", "Mures"],
          disappointed: ["Õnnetu", "Pettunud", "Rahulolematu"],
          indifferent: ["Hoolimatu", "Ükskõikne", "Saamatu"],
          peaceful: ["Vaikne", "Rahulik", "Usaldav"],
          satisfied: ["Kindel", "Rahul", "Heakskiitev"]
        },
        en: {
          enthusiastic: ["Happy", "Enthusiastic", "Interested"],
          surprised: ["Amazed", "Surprised", "Impressed"],
          irritated: ["Furious", "Irritated", "Indignant"],
          confused: ["Stressed", "Confused", "Scared"],
          disappointed: ["Unhappy", "Disappointed", "Bored"],
          indifferent: ["Unconcerned", "Indifferent", "Incapable"],
          peaceful: ["Trusting", "Peaceful", "Tranquil"],
          satisfied: ["Confident", "Satisfied", "Accepting"]
        },
        ru: {
          enthusiastic: ["Веселый", "Увлеченный", "Заинтересованный"],
          surprised: ["Изумленный", "Удивленный", "Впечатленный"],
          irritated: ["Разгневанный", "Раздражённый", "Возмущенный"],
          confused: ["Напряженный", "Растерянный", "Cтрах"],
          disappointed: ["Недовольный", "Разочарованный", "Скучающий"],
          indifferent: ["Безразличный", "Равнодушный", "Неспособный"],
          peaceful: ["Доверительный", "Спокойный", "Умиротворённый"],
          satisfied: ["Уверенный", "Довольный", "Одобряющий"]
        },
        de: {
          enthusiastic: ["Glücklich", "Begeistert", "Interessiert"],
          surprised: ["Erstaunt", "Überrascht", "Beeindruckt"],
          irritated: ["Wütend", "Irritiert", "Empört"],
          confused: ["Gestresst", "Verwirrt", "Ängstlich"],
          disappointed: ["Unglücklich", "Enttäuscht", "Gelangweilt"],
          indifferent: ["Unbekümmert", "Gleichgültig", "Antriebslos"],
          peaceful: ["Zutraulich", "Ruhig", "Gelassen"],
          satisfied: ["Zuversichtlich", "Zufrieden", "Akzeptierend"]
        },
        tr: {
          enthusiastic: ["Mutlu", "Heyecanlı", "İllgili"],
          surprised: ["Şoktayım", "Şaşkın", "Etkileyici"],
          irritated: ["Sinirli", "Tedirgin", "Kizgın"],
          confused: ["Stresli", "Karışık", "Hüzünlü"],
          disappointed: ["Mutsuz", "Bezgin", "Sıkılgan"],
          indifferent: ["Kaygısız", "Umursamaz", "Beceriksiz"],
          peaceful: ["Aşina", "Sakin", "Rahat"],
          satisfied: ["Güvenilir", "Memnun", "Kabullenmek"]
        }
      }

      return allEmotionOptions[currentLanguage] || {}
    },

    // Map of emotion group name and main emotion id
    emotionIdMap: () => {
      return {
        enthusiastic: 22,
        surprised: 12,
        irritated: 82,
        confused: 72,
        disappointed: 62,
        indifferent: 52,
        peaceful: 42,
        satisfied: 32
      }
    }
  },
  mutations: {
    // UI
    setWindowSize (state, payload) {
      state.ui.windowWidth = payload.width
      state.ui.windowHeight = payload.height
    },
    setIsWidget (state, newStatus) {
      state.ui.isWidget = newStatus
    },
    setIsEmbed (state, newStatus) {
      state.ui.isEmbed = newStatus
    },
    setBackground (state, payload) {
      state.ui.emotionBackground = payload.image
    },
    setEmotionsImage (state, payload) {
      state.ui.emotionsImage = payload.image
    },
    setSendingAnswer (state, payload) {
      state.ui.isSendingAnswer = payload.newStatus
    },

    setLanguage (state, newLanguage) {
      state.ui.selectedLanguage = newLanguage
      state.answer.language = newLanguage
    },
    setLanguageSupport (state, languageSupportList) {
      state.ui.languageSupport = languageSupportList
    },

    updateConfig (state, payload) {
      // Update default store config with new values
      let newConfig = Object.assign({}, state.config, payload.config)
      state.config = newConfig
    },

    // Survey data
    setSurveyData (state, newSurvey) {
      state.survey = newSurvey
      state.answer.project_id = newSurvey.project_id
    },
    setQuestionsData (state, newQuestions) {
      state.questions = newQuestions
    },
    setSurveyProfileData (state, payload) {
      state.surveyProfile = payload
    },
    setStatsLoaded (state, payload) {
      state.ui.statsLoaded = payload.newStatus
    },

    // Steps
    setStep (state, payload) {
      state.ui.step = payload.nextStep
    },

    // Answers
    setSelectedEmotionGroup (state, payload) {
      state.answer.emotionGroup = payload.emotionGroup
      state.answer.emotion = payload.emotion
      state.answer.emotion_id = payload.emotionId
    },
    setSelectedEmotion (state, payload) {
      state.answer.emotion = payload.emotion
      state.answer.emotion_id = payload.emotionId
    },
    setReferenceCode (state, payload) {
      state.answer.referenceCode = payload.referenceCode
    },
    setAnswer (state, payload) {
      let questionId = payload.questionId
      let answerValue = payload.value

      // Set regular question answer, q_id 1-8
      if (questionId <= 8) {
        state.answer.questions[questionId].value = answerValue
      } else {
        // Set additional question answer, q_id 8->
        if (!state.answer.questions[questionId]) {
          state.answer.questions[questionId] = {value: answerValue, extra: ''}
        } else {
          state.answer.questions[questionId].value = answerValue
        }
      }
    },
    setAnswerExtra (state, payload) {
      let questionId = payload.questionId
      let answerExtra = payload.value

      // Set regular question answer, q_id 1-8
      if (questionId <= 8) {
        state.answer.questions[questionId].extra = answerExtra
      } else {
        // Set additional question answer, q_id 8->
        if (!state.answer.questions[questionId]) {
          state.answer.questions[questionId] = {value: '', extra: answerExtra}
        } else {
          state.answer.questions[questionId].extra = answerExtra
        }
      }
    }
  },
  actions: {
    // UI
    setWindowSize ({ commit }, payload) {
      commit('setWindowSize', payload)
    },
    loadBackgroundImage ({ commit }, payload) {
      if(payload.imageUrl) {
        let img = new Image()

        img.addEventListener('load', function() {
          commit('setBackground', {image: img})
        }, false)

        img.src = payload.imageUrl
      }
    },
    loadEmotionsImage ({ commit }, payload) {
      if(payload.imageUrl) {
        let img = new Image()

        img.addEventListener('load', function() {
          commit('setEmotionsImage', {image: img})
        }, false)

        img.src = payload.imageUrl
      }
    },
    setIsWidget ({ commit }, payload) {
      commit('setIsWidget', payload.newStatus)
    },
    setIsEmbed ({ commit }, payload) {
      commit('setIsEmbed', payload.newStatus)
    },
    setLanguage ({ commit }, payload) {
      commit('setLanguage', payload.language)
    },

    // Change step
    setStep ({ commit }, payload) {
      commit('setStep', payload)
    },

    // Answers
    setSelectedEmotionGroup ({ commit }, payload) {
      // Select main emotion and change set
      commit('setSelectedEmotionGroup', payload)
      commit('setStep', {nextStep: 'details'})
    },
    setSelectedEmotion ({ commit }, payload) {
      // Update with sub emotion selection
      commit('setSelectedEmotion', payload)
    },
    setAnswer ({ commit }, payload) {
      commit('setAnswer', payload)
    },
    setAnswerExtra ({ commit }, payload) {
      commit('setAnswerExtra', payload)
    },
    startSendAnswer ({ dispatch, getters }) {
      let answerData = getters.answers
      let sendData = {
        time: new Date(),
        project_id: answerData.project_id,
        emotion_id: answerData.emotion_id,
        emotion: answerData.emotion,
        language: answerData.language,
        reference_code: answerData.referenceCode || '',
        a2: getters.mergedAnswer(2),
        a3: getters.mergedAnswer(3),
        a4: getters.mergedAnswer(4),
        a5: getters.mergedAnswer(5),
        a6: getters.mergedAnswer(6),
        a7: getters.mergedAnswer(7),
        a8: getters.mergedAnswer(8),
        extra_questions: {
          // "a9": "Kyllä",
          // "a10": "Ei",
          // "a11": "Ei",
          // ...
        }
      }

      answerData.questions
        .map((question, questionId) => {
          // Pack extra questions into separate object
          if (questionId > 8) {
            sendData.extra_questions['a' + questionId] = getters.mergedAnswer(questionId)
          }
        })

      dispatch('sendAnswer', {data: sendData}).then(() => {
        dispatch('getSurveyProfile', {surveyId: answerData.project_id}).then(() => {
          // Answers sent, switch to end step
          dispatch('setStep', {nextStep: 'end'})
        }).catch(() => {
          dispatch('setStep', {nextStep: 'end'})
        })
      })
    },

    // Init survey
    initSurvey ({ commit, dispatch }, payload) {
      let config = {}
      if (window.vibevisionSurveyConfig) {
        config = window.vibevisionSurveyConfig
      }

      if (!config.surveyAddress) {
        let urldata = window.location.href // get the url used for launching the application
        let pid = urldata.search("vibevision")

        let url
        if (pid == -1) {          // source is not vibevision -> demo project selected
          url = "demo.vibevision.io"
        } else {
          url = window.location.hostname
        }

        config.surveyAddress = url
      }

      commit('updateConfig', {config: config})

      // TODO Dispatch as promise and change to next step when both resolve?
      dispatch('loadBackgroundImage', {imageUrl: payload.backgroundImageUrl})
      dispatch('getSurvey', {surveyUrl: config.surveyAddress})

      // Dispatch emotion circle image loading late as it is not needed directly after load
      dispatch('loadEmotionsImage', {imageUrl: payload.emotionsImageUrl})
    },

    // Use all loaded information to determine starting language for survey
    // Priority order: config, survey default, first in supported list, finnish
    //
    // TODO Resolve conflict with config/survey language not present in actually supported list
    setDefaultLanguage ({ dispatch, getters }) {
      let configLanguage = getters.config.widgetLanguage
      let surveyLanguage = getters.survey.language
      let languageSupport = getters.supportedLanguages

      let surveyStartLanguage = 'fi'

      if (configLanguage) {
        surveyStartLanguage = configLanguage
      } else if(surveyLanguage) {
        surveyStartLanguage = surveyLanguage
      } else {
        let languageMap = [
          {supportKey: 'finnish', languageKey: 'fi'},
          {supportKey: 'swedish', languageKey: 'se'},
          {supportKey: 'english', languageKey: 'en'},
          {supportKey: 'russian', languageKey: 'ru'},
          {supportKey: 'estonian', languageKey: 'es'},
          {supportKey: 'german', languageKey: 'de'},
          {supportKey: 'turkish', languageKey: 'tr'},
        ]

        let foundLanguage = languageMap.find(
          mapping => languageSupport[mapping.supportKey] == true
        )

        surveyStartLanguage = foundLanguage.languageKey
      }

      dispatch('setLanguage', {language: surveyStartLanguage})
    },

    // Fetch survey data with URL
    getSurvey ({ commit, dispatch }, payload) {
      return Client().get(
        "/address", {params: {url: payload.surveyUrl}}
      ).then(response => {
        let reponseProject = {}
        let projectData = {}

        if (response.data.length > 0 && response.data[0].active == true) {
          reponseProject = response.data[0]
          projectData.project_id = reponseProject.project_id
          projectData.db_name = reponseProject.t_name
          projectData.language = reponseProject.default_language
          projectData.languages = reponseProject.languages

          // Handle logo file
          projectData.logo_file = 'img/default_logo.png'
          //$scope.logo_file = 'img/default_logo.png'
          if (reponseProject.logo_file) {
            projectData.logo_file = 'https://hallinta.vibevision.io/img/logo/' + reponseProject.logo_file
            //$scope.logo_file = projectData.logo_file
          }

          // Handle end options fields
          if (reponseProject.end_text_options) {
            projectData.end_text_options = JSON.parse(reponseProject.end_text_options)
          }
          if (reponseProject.end_button_options) {
            projectData.end_button_options = JSON.parse(reponseProject.end_button_options)
          }

          // Update survey language support
          let languageSupport = {}
          if (projectData.languages.indexOf('fi') !== -1) { languageSupport.finnish = true }
          if (projectData.languages.indexOf('se') !== -1) { languageSupport.swedish = true }
          if (projectData.languages.indexOf('en') !== -1) { languageSupport.english = true }
          if (projectData.languages.indexOf('ru') !== -1) { languageSupport.russian = true }
          if (projectData.languages.indexOf('ee') !== -1) { languageSupport.estonian = true }
          if (projectData.languages.indexOf('de') !== -1) { languageSupport.german = true }
          if (projectData.languages.indexOf('tr') !== -1) { languageSupport.turkish = true }
          commit('setLanguageSupport', languageSupport)

          // Set loaded survey data to store and fetch questions
          commit('setSurveyData', projectData)
          dispatch('getQuestions', {surveyId: projectData.project_id}).then(() => {
            // Questions loaded, display emotion selection step
            dispatch('setDefaultLanguage')
            dispatch('setStep', {nextStep: 'emotion'})
          })
        } else {
          // eslint-disable-next-line
          console.log('No active survey found')
          throw new Error('No active survey found')
        }
      })
      .catch(error => {
        // eslint-disable-next-line
        console.log(error)
        throw error
      })
    },

    // Fetch questions/translations
    getQuestions ({ commit }, payload) {
      return Client().get(
        "/questions", {params: {id: payload.surveyId}}
      ).then(response => {
        let reponseQuestions = response.data
        let questionsData = {
          fi: [],
          se: [],
          en: [],
          ru: [],
          es: [],
          de: [],
          tr: []
        }

        if (response.data.length > 0) {
          reponseQuestions.forEach((question) => {
            // Parse/merge general and language specific question options
            if (typeof question.question_options !== "undefined" || typeof question.language_options !== "undefined") {
              var question_options = JSON.parse(question.question_options) || {}
              var language_options = JSON.parse(question.language_options) || {}

              question.question_options = Object.assign({}, question_options, language_options)

              // Handle option encoding
              if (typeof question.question_options.other_input !== "undefined") {
                question.question_options.other_input = (question.question_options.other_input === '1')
              }
              if (typeof question.question_options.min !== "undefined") {
                question.question_options.min = parseInt(question.question_options.min, 10)
              }
              if (typeof question.question_options.max !== "undefined") {
                question.question_options.max = parseInt(question.question_options.max, 10)
              }

              // Force store answer data type to array for multiple checkbox questions
              if (question.question_type == 'multiple_multi') {
                commit(
                  'setAnswer',
                  {
                    questionId: question.q_id,
                    value: []
                  }
                )
              }
            }

            questionsData[question.language].push(question)
          })

          commit('setQuestionsData', questionsData)
        } else {
          // eslint-disable-next-line
          console.log('No survey questions found')
          throw new Error('No survey questions found')
        }
      })
      .catch(error => {
        // eslint-disable-next-line
        console.log(error)
        throw error
      })
    },

    // Send answer to API at the end of survey
    sendAnswer ({ commit, getters }, payload) {
      commit('setSendingAnswer', {newStatus: true})

      // /!\ QUICK HACK /!\
      // Regular Angular survey page now generates unique id for answer and saves
      // answer partially after emotion has been selected with '/einfoUid' URL and
      // updates with full answer data with '/einfo' URL at the end with existing
      // unique id.
      //
      // Widget was not updated to handle this new two step process and there is not
      // time to change widget process to match backend changes.
      //
      // As workaround widget generates unique id and performs partial save call with
      // full answer data at the end of survey. This should create answer correctly
      // in database but partial save data is missed if widget is closed.
      payload.data.uid = function() {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
          var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
          return v.toString(16);
        });
      }() // Sets up function and calls it directly

      let saveUrl = '/einfoUid' // Partial new answer save URL
      // let saveUrl = '/einfo' // Full answer update URL

      // TODO Build proper separate sending function and generate unique id when
      // survey is (re)started.

      if(getters.config.debug) {
        // eslint-disable-next-line
        console.log(payload)

        return new Promise((resolve, reject) => {
          window.setTimeout(
            () => {
              try {
                commit('setSendingAnswer', {newStatus: false})
                resolve()
              } catch(error) {
                reject(error)
              }
            },
            10
          )
        })
      }

      return Client().post(
        saveUrl,
        payload.data,
        {
          timeout: 2000,
          headers: {
            'Accept': '*/*',
            'Content-Type': 'text/plain;charset=UTF-8'
          }
        }
      ).then(() => {
        commit('setSendingAnswer', {newStatus: false})
      })
      .catch(error => {
        commit('setSendingAnswer', {newStatus: false})

        // eslint-disable-next-line
        console.log(error)
        throw error
      })
    },

    // Fetch end step survey answer profile data
    getSurveyProfile ({ commit }, payload) {
      return Client().get(
        "/eprofile",
        {
          params: {id: payload.surveyId},
          timeout: 2000
        }
      ).then(response => {
        commit('setSurveyProfileData', response.data)
        commit('setStatsLoaded', {newStatus: true})
      })
      .catch(error => {
        // eslint-disable-next-line
        console.log(error)
        throw error
      })
    }
  },
  modules: {
  }
})
